// FO-012 (batch-14) — reserveringsregister: per medewerker per kalendermaand de
// uitkomst van de periode-saldering + de te reserveren mutatie-componenten die de
// verloning van de VOLGENDE periode voeden. DTOs + Zod-schemas (gedeeld door API
// en backoffice-form zodat het form niets kan sturen dat de API weigert).

import { z } from 'zod'
import type { OverurenTredenDTO, ToeslagUrenDTO } from './payroll'

/**
 * De saldo-emmers waartegen een (maand-net) min-uren zinvol kan worden afgeboekt
 * (FO-013). Subset van `LeaveEntitlementType`: de uitbetaalbare/opneembare
 * potjes — niet senioren/ouderschap (die kennen een eigen, niet-verrekenbaar
 * regime). `TVT_WERK` is het standaardvoorstel (overuren-tijdbank). Lokaal
 * gedefinieerd omdat @streep/types geen gedeelde emmer-enum-const heeft.
 */
export const PAYROLL_AFBOEK_EMMER = [
  'TVT_WERK',
  'TVT_REIS',
  'ATV',
  'BOVENWETTELIJK',
  'WETTELIJK',
] as const
export type PayrollAfboekEmmer = (typeof PAYROLL_AFBOEK_EMMER)[number]

export const PAYROLL_AFBOEK_EMMER_LABELS: Record<PayrollAfboekEmmer, string> = {
  TVT_WERK:       'TVT werk',
  TVT_REIS:       'TVT reis',
  ATV:            'ATV',
  BOVENWETTELIJK: 'Bovenwettelijk verlof',
  WETTELIJK:      'Wettelijk verlof',
}

export const PAYROLL_RESERVERING_STATUS = [
  'CONCEPT',
  'VASTGELEGD',
  'VERWERKT',
  'INGETROKKEN',
] as const
export type PayrollReserveringStatus = (typeof PAYROLL_RESERVERING_STATUS)[number]

export const PAYROLL_RESERVERING_STATUS_LABELS: Record<PayrollReserveringStatus, string> = {
  CONCEPT:     'Concept (live berekend)',
  VASTGELEGD:  'Vastgelegd ter beoordeling',
  VERWERKT:    'Verwerkt in verloning',
  INGETROKKEN: 'Ingetrokken',
}

export const PAYROLL_RESERVERING_STATUS_BADGE: Record<
  PayrollReserveringStatus,
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  CONCEPT:     'secondary',
  VASTGELEGD:  'info',
  VERWERKT:    'success',
  INGETROKKEN: 'danger',
}

/** Uitkomst van de periode-saldering, gespiegeld aan de pure helper in @streep/utils. */
export const PERIODE_SALDERING_STATUS = ['TEKORT', 'GELIJK', 'OVERSCHOT'] as const
export type PeriodeSalderingStatus = (typeof PERIODE_SALDERING_STATUS)[number]

export const PERIODE_SALDERING_STATUS_LABELS: Record<PeriodeSalderingStatus, string> = {
  TEKORT:    'Tekort',
  GELIJK:    'Gelijk',
  OVERSCHOT: 'Overschot',
}

/**
 * Eén reservering-regel per medewerker per periode. Gebruikt voor zowel de live
 * preview (`id` = null, `status` = 'CONCEPT') als de vastgelegde snapshot
 * (`id` gevuld). Alle bedragen/uren als `number` aan de DTO-grens; de API rekent
 * met Decimal en rondt pas op het eind af.
 */
export interface PayrollReserveringRowDTO {
  /** null bij een live preview-regel (nog niet vastgelegd). */
  id: string | null
  employeeId: string
  employeeNumber: string
  naam: string
  /**
   * payroll-werkruimte 18→8 — BV van de medewerker (voor het BV-filter op het
   * scherm). Alleen gestempeld door `getReserveringOverzicht`; interne
   * consumenten van de kale rijen (verloningsperiode) laten hem weg.
   */
  entityId?: string | null
  status: PayrollReserveringStatus

  // ── Periode-saldering ──
  honderdProcentUren: number
  contractUrenPeriode: number
  doorbetaaldeAfwezigheidUren: number
  effectieveNorm: number
  saldoUren: number
  salderingStatus: PeriodeSalderingStatus

  // ── Te reserveren componenten (per component traceerbaar) ──
  overschotUren: number
  overschotBedrag: number
  overwerkUren: number
  /**
   * FO-305 P4 (batch-33) — de overwerk-uren per CAO-trede (bevroren in het
   * snapshot; oude snapshots hebben 0 en tonen de som `overwerkUren`).
   * `p100 + p125 + p150 + p200 === overwerkUren`.
   */
  overwerkUrenTreden: OverurenTredenDTO
  overwerkPremieBedrag: number
  ploegToeslagBedrag: number
  zaterdagToeslagBedrag: number
  zondagToeslagBedrag: number
  feestdagToeslagBedrag: number
  overigeToeslagBedrag: number
  /**
   * FO-305 P6 (batch-33) — toeslag-uren per ploegsoort + za/zo (+ feestdag/overig
   * in detail). Bevroren in het snapshot; oude snapshots tonen 0.
   */
  toeslagUren: ToeslagUrenDTO
  premieTotaalBedrag: number
  verrekendeReisUren: number
  resterendeReisUren: number
  resterendeReisBedrag: number
  /**
   * De doorbelaste km-vergoeding (klantfacturatie). FO-411 (batch-54): in
   * NIEUWE snapshots is dit puur INFORMATIEF - het telt niet meer mee in
   * `totaalBedrag`, want kilometers zijn geen looncomponent. Snapshots van vóór
   * de fix dragen hier het oude BRUTO-bedrag dat toen wél in `totaalBedrag` zat;
   * die worden nooit stil gemuteerd (opnieuw vastleggen herrekent ze).
   */
  reisVergoeding: number
  /**
   * FO-411 (batch-54) — het deel van de km-vergoeding dat WEL wordt uitbetaald
   * (regeling "eigen vervoer", per leg-datum) en dus als enige km-post in
   * `totaalBedrag` meetelt. 0 in snapshots van vóór de fix: staat daar een
   * bedrag in `reisVergoeding`, dan is dat een OUDE bruto-reservering.
   */
  kmVergoedingNetto: number
  /**
   * FO-411 (batch-54) — server-bepaald signaal: in dit (bevroren) snapshot zat de
   * DOORBELASTE km-vergoeding nog ín `totaalBedrag`, wat betekent dat het van
   * vóór de km-fix dateert. Afgeleid uit de cijfers (totaal min alle andere
   * componenten == de doorbelaste km), NIET uit "kmVergoedingNetto is 0" - dat
   * laatste geldt ook voor elke correcte nieuwe reservering van een medewerker
   * zonder de eigen-vervoer-regeling. `false` voor live-berekende rijen.
   */
  kmZatInTotaal: boolean
  declaratiesTotaal: number
  totaalBedrag: number

  // ── Tekort-signaal ── > 0 = min-uren naar HR (contractsalaris blijft).
  restTekortUren: number

  /** CAO-premie-keuze waarmee de regel is berekend (true = per week). */
  premiePerWeek: boolean
  /** Navolgbare opbouwregel (FO §10). */
  opbouwregel: string

  /** Alleen gevuld bij een vastgelegde snapshot. */
  snapshotOp: string | null
}

/** Periode-totalen voor de overzicht-kop. */
export interface PayrollReserveringTotalenDTO {
  aantalMedewerkers: number
  totaalBedrag: number
  aantalMetTekort: number
  totaalTekortUren: number
}

export interface PayrollReserveringOverzichtDTO {
  year: number
  month: number
  /** True zodra er voor deze periode een VASTGELEGD snapshot is (rijen zijn dan frozen). */
  isVastgelegd: boolean
  /** De CAO-premie-keuze die voor deze (preview-)berekening is toegepast. */
  premiePerWeek: boolean
  rows: PayrollReserveringRowDTO[]
  totalen: PayrollReserveringTotalenDTO
}

// ─── Zod request schemas ──────────────────────────────────────────────────────

const yearSchema = z.coerce.number().int().min(2000).max(2100)
const monthSchema = z.coerce.number().int().min(1).max(12)

export const reserveringQuerySchema = z.object({
  year:  yearSchema,
  month: monthSchema,
})
export type ReserveringQueryInput = z.infer<typeof reserveringQuerySchema>

export const snapshotReserveringSchema = z.object({
  year:  yearSchema,
  month: monthSchema,
})
export type SnapshotReserveringInput = z.infer<typeof snapshotReserveringSchema>

// ─── Instelling (CAO-premie + standaard-afboek-emmer) ─────────────────────────

export interface PayrollReserveringInstellingDTO {
  /** true = overwerk-premie per week (CAO M&T, default); false = periode-net. */
  premiePerWeek: boolean
  /** Standaard voorgestelde afboek-emmer voor maand-net min-uren (FO-013). */
  minUrenStandaardAfboekEmmer: PayrollAfboekEmmer
  toelichting: string | null
  /** FO-325 — plausibiliteitsdrempel (EUR) voor de payroll-controle-preview. */
  plausibiliteitsDrempel: number
  updatedAt: string | null
}

export const updateReserveringInstellingSchema = z.object({
  premiePerWeek:               z.boolean().optional(),
  minUrenStandaardAfboekEmmer: z.enum(PAYROLL_AFBOEK_EMMER).optional(),
  toelichting:                 z.string().trim().max(500).nullable().optional(),
  // FO-325 — plausibiliteitsdrempel; > 0 (zacht signaal, geen blokkade).
  plausibiliteitsDrempel:      z.number().positive().max(1_000_000).optional(),
})
export type UpdateReserveringInstellingInput = z.infer<typeof updateReserveringInstellingSchema>
